"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";

// Define the form schema with Zod
const videoLinkFormSchema = (t: any) =>
  z.object({
    youtube_url: z
      .string()
      .min(1, { message: t("videoLink.urlRequired") })
      .regex(
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\S+)?/,
        t("videoLink.invalidUrl"),
      ),
  });

type VideoLinkFormValues = z.infer<ReturnType<typeof videoLinkFormSchema>>;

interface VideoLinkDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (url: string) => void;
  initialUrl?: string | null;
}

export function VideoLinkDialog({ isOpen, onClose, onSuccess, initialUrl }: VideoLinkDialogProps) {
  const { t } = useTranslation(["classroom", "common"]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize the form
  const form = useForm<VideoLinkFormValues>({
    resolver: zodResolver(videoLinkFormSchema(t)),
    defaultValues: {
      youtube_url: initialUrl || "",
    },
  });

  // Handle form submission
  const onSubmit = async (data: VideoLinkFormValues) => {
    try {
      setIsSubmitting(true);

      // Extract video ID from URL
      const match = data.youtube_url.match(
        /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      );

      if (match && match[1]) {
        // Return the full URL to parent
        onSuccess(data.youtube_url);
        onClose();
        form.reset();
      } else {
        throw new Error(t("videoLink.invalidUrl"));
      }
    } catch (error: any) {
      console.error("Failed to set video URL:", error);
      toast.error(t("common:error"), {
        description: error.message || t("videoLink.errorDescription"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-side-accent/80 text-accent-foreground max-h-[90dvh] max-w-2xl overflow-hidden rounded-2xl border-none p-0 shadow-2xl backdrop-blur-xl max-sm:h-[100dvh] max-sm:max-h-dvh max-sm:rounded-none">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="from-accent-foreground/40 to-accent-foreground/60 absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br" />
          <div className="from-accent-foreground/30 to-accent-foreground/50 absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr" />
        </div>

        <ScrollArea className="relative max-h-[90dvh] max-sm:max-h-[100dvh]">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="p-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader className="mb-6">
              <DialogTitle className="text-accent-foreground flex items-center gap-3 text-xl font-semibold">
                <div className="bg-muted/20 flex h-10 w-10 items-center justify-center rounded-xl backdrop-blur-sm">
                  <Icon
                    className="text-accent-foreground"
                    height="1.25rem"
                    icon="lucide:play-circle"
                    width="1.25rem"
                  />
                </div>
                {initialUrl ? t("videoLink.updateTitle") : t("videoLink.title")}
              </DialogTitle>
            </DialogHeader>

            <Form {...form}>
              <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-side-accent/50 border-border/50 rounded-xl border p-5 backdrop-blur-sm"
                  initial={{ opacity: 0, y: 10 }}
                  transition={{ delay: 0.2 }}
                >
                  <FormField
                    control={form.control}
                    name="youtube_url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-accent-foreground flex items-center gap-2">
                          <Icon height="1rem" icon="mdi:youtube" width="1rem" />
                          {t("videoLink.youtubeUrl")}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            className="bg-background/80 border-border/50"
                            placeholder={t("videoLink.urlPlaceholder")}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </motion.div>

                <DialogFooter className="flex gap-3">
                  <Button
                    className="bg-muted/50 hover:bg-muted/30 border-border/50 rounded-xl"
                    disabled={isSubmitting}
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                  >
                    {t("common:cancel")}
                  </Button>
                  <Button
                    className="bg-accent-foreground text-primary hover:bg-accent-foreground/80 rounded-xl"
                    disabled={isSubmitting}
                    type="submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Icon
                          className="mr-2 animate-spin"
                          height="1rem"
                          icon="lucide:loader-2"
                          width="1rem"
                        />
                        {t("common:processing")}
                      </>
                    ) : (
                      <>
                        <Icon className="mr-2" height="1rem" icon="lucide:check" width="1rem" />
                        {initialUrl ? t("videoLink.update") : t("videoLink.set")}
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </motion.div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
